"use client";

import React, { memo } from 'react';
import { useSDUIFlowLayoutItemOnVisibilityRendererFlags, useSDUIFlowLayoutItemOnVisibilityRendererSetFlag } from './SDUIFlowLayoutItemOnVisibilityRendererContext';
import { useDoActionWhenVisible } from '@faire/web--source/ui/hooks/useDoActionWhenVisible';

interface Props {
  flagIndex: number;
  shouldUpdateFlagOnVisibility?: boolean;
  component: React.ReactNode;
}

export const SDUIFlowLayoutItemOnVisibilityRenderer = ({
  flagIndex,
  shouldUpdateFlagOnVisibility = true,
  component,
}: Props) => {
  const flags = useSDUIFlowLayoutItemOnVisibilityRendererFlags();
  const setFlag = useSDUIFlowLayoutItemOnVisibilityRendererSetFlag();
  const ref = useDoActionWhenVisible(() => {
    if (shouldUpdateFlagOnVisibility) {
      setFlag?.(flagIndex, true);
    }
  }, {
    rootMargin: `-100px 0px 0px 0px`,
  });
  const isTopAnchorVisible = !!flags?.isTopAnchorVisible;
  // Make sure the isTopAnchorVisible is first true before rendering the rest of the items
  // This is to ensure that the first item is rendered when the page is scrolled to the top
  const flagValue = !!flags?.[flagIndex];
  // This will ensure that ref is attached when scroll to top is finished
  const shouldAttachRef = shouldUpdateFlagOnVisibility && !flagValue && isTopAnchorVisible;
  return <MemoizedInnerItem flagValue={flagValue} component={component} ref={shouldAttachRef ? ref : undefined} />;
};

interface InnerProps {
  flagValue: boolean;
  component: React.ReactNode;
  ref?: React.RefCallback<HTMLDivElement>;
}

// eslint-disable-next-line @faire/ban-top-level-request-specific-data, @faire/no-inline-hoc-fc
const MemoizedInnerItem = memo(({
  flagValue,
  component,
  ref,
}: InnerProps) => {
  if (flagValue) {
    return component;
  }

  return <div ref={ref} className="w-full h-[300px] lg:h-[400px]"></div>;
});
